import { FC } from 'react';
import { motion } from 'framer-motion';
import { User, Bell, Wifi, WifiOff } from 'lucide-react';
import NewChatIcon from '../icons/professional/NewChatIcon.js';
import { useChatStore } from '../../stores/useChatStore.js';
import { useChatSocket } from '../../hooks/useChatSocket.js';
import { useEditorSocket } from '../../hooks/useEditorSocket.js';
import { useBeeHealth } from '../../hooks/useBeeHealth.js';


// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface BeehiveTopNavProps {}

const BeehiveTopNav: FC<BeehiveTopNavProps> = () => {
  const { taskMetadata, sessionId } = useChatStore();
  
  const chatSocket = useChatSocket({
    sessionId: sessionId || undefined,
    userId: `user-${Math.random().toString(36).substr(2, 9)}`
  });
  
  const editorSocket = useEditorSocket({
    sessionId: sessionId || undefined,
    userId: `user-${Math.random().toString(36).substr(2, 9)}`
  });
  
  const { beeHealth } = useBeeHealth();



  const playNavigationAudio = () => {
    try {
      const bell = new Audio('/sounds/bell.mp3');
      bell.play().catch(() => {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance('Navigation');
          utterance.volume = 0.1;
          utterance.rate = 1.5;
          window.speechSynthesis.speak(utterance);
        }
      });
    } catch {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance('Navigation');
        utterance.volume = 0.1;
        utterance.rate = 1.5;
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  return (
    <header className="relative bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 border-b border-slate-800 h-24 flex items-center px-6 overflow-hidden">
      {/* Honeycomb texture overlay */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none z-[1]"
        style={{
          backgroundImage: 'url(/images/beehive-texture.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      {/* Smoked Opaque Overlay for glass-dark header */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] z-[2]"></div>
      
      <div className="relative z-10 flex items-center space-x-2 mr-8">
        <img 
          src="/assets/logos/beehive-logo-transparent.png" 
          alt="Beehive" 
          className="w-8 h-8"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        <div className="relative">
          <h1 className="text-xl font-bold violett-gradient-text text-drop-shadow">HiveCore</h1>
          <p className="text-xs cream-gradient-text text-drop-shadow">Autonomous AI Workspace • SOMA Enabled</p>
        </div>
      </div>



      <div className="relative z-10 flex items-center space-x-4 ml-8 flex-1 justify-end">
        <div className="relative text-right">
          <p className="text-sm font-medium violett-gradient-text text-drop-shadow">
            {taskMetadata?.title || 'Workspace Session'}
          </p>
          <p className="text-xs cream-gradient-text text-drop-shadow">
            {sessionId ? `Session: ${sessionId.slice(-8)}` : 'No active session'}
          </p>
        </div>

        <motion.button
          onClick={playNavigationAudio}
          className="relative p-2 rounded-lg bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 text-white hover:text-white transition-colors overflow-hidden"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Smoked overlay for glass-dark button */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
          <Bell className="relative z-10 w-4 h-4" />
        </motion.button>


        <motion.button
          className="relative p-2 rounded-lg bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 text-white hover:text-white transition-colors overflow-hidden"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Smoked overlay for glass-dark button */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
          <User className="relative z-10 w-4 h-4" />
        </motion.button>

        <motion.button
          className="relative p-2 rounded-lg bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 text-white hover:text-white transition-colors overflow-hidden"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Chat"
        >
          {/* Smoked overlay for glass-dark button */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
          <NewChatIcon className="relative z-10 w-4 h-4" />
        </motion.button>


        {/* Simple Connection Status Indicator - positioned at end */}
        <motion.div
          className="relative bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 px-4 py-2 overflow-hidden"
          whileHover={{ scale: 1.02 }}
          title="Connection Status"
        >
          {/* Smoked overlay for glass-dark effect */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
          
          <div className="relative z-10 flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <span className="text-xs cream-gradient-text text-drop-shadow font-medium">Agentic System:</span>
                <div className={`w-2 h-2 rounded-full ${
                  beeHealth?.summary?.healthy === beeHealth?.summary?.total ? 'bg-green-400' : 
                  (beeHealth?.summary?.healthy || 0) > (beeHealth?.summary?.total || 0) / 2 ? 'bg-yellow-400' : 'bg-red-400'
                }`}></div>
              </div>
              <div className="w-px h-4 bg-slate-600/50"></div>
              <div className="flex items-center space-x-1">
                <span className="text-xs cream-gradient-text text-drop-shadow font-medium">WebSocket:</span>
                <div className={`w-2 h-2 rounded-full ${
                  (chatSocket.status === 'open' && editorSocket.status === 'open') ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
              </div>
            </div>
            
            {/* Overall connection icon */}
            <div className="flex items-center">
              {(beeHealth?.summary?.healthy === beeHealth?.summary?.total && chatSocket.status === 'open' && editorSocket.status === 'open') ? (
                <Wifi className="w-3 h-3 text-white" />
              ) : (
                <WifiOff className="w-3 h-3 text-white" />
              )}
            </div>
          </div>
        </motion.div>

        {/* BeeHive Logo positioned at absolute far right - flat on top bar */}
        <motion.div
          className="relative ml-6"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img 
            src="/assets/logos/beehive-logo.png" 
            alt="BeeHive Logo" 
            className="w-8 h-8 drop-shadow-lg filter brightness-110 hover:brightness-125 transition-all duration-300"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </motion.div>
      </div>


    </header>
  );
};

export default BeehiveTopNav;
